import { faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { OAAnyOtherAssetsDetailsForm } from ".";
import { IAnyOtherAssets, IFormITR1, IReviewCapitalGains } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const OAAnyOtherAssetsDetails: React.FC = () => {
	const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<IAnyOtherAssets | null>(null);
	const [activeItem, setActiveItem] = useState<IAnyOtherAssets | null>(null);
	// used in both edit and delete cases
	const [activeItemIdx, setActiveItemIdx] = useState<string>("");

	const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
	const AnyOtherAssetsDetails = cloneDeep(values.IncomeDetails?.OtherAssets || []);

	const handleDeleteInterestItem = async () => {
		AnyOtherAssetsDetails.splice(Number(activeItemIdx), 1);
		setFieldValue("IncomeDetails.OtherAssets", AnyOtherAssetsDetails);
		setShowDeleteModal(() => null);
		setActiveItem(() => null);
		setActiveItemIdx(() => "");
		handleTotalCalculation();
	};

	const handleTotalCalculation = () => {
		const AnyOtherAssetsGains =
			AnyOtherAssetsDetails?.reduce((acc, curr) => {
				if (curr?.ImprovementExpenses?.length) {
					curr?.ImprovementExpenses?.forEach((item) => {
						if (item.length && item[1]) {
							acc += parseInt(item[1]) || 0;
						}
					});
				}

				const profitOrLoss =
					Number(curr?.TotalSalePrice || 0) -
					Number(curr?.TotalPurchasePrice || 0) -
					Number(curr?.TransferExpense || 0) -
					acc;
				return profitOrLoss;
			}, 0) || 0;

		const CapitalGains: IReviewCapitalGains = {
			...values.CapitalGains,
			TotalAnyOtherAssetsGain: AnyOtherAssetsGains
		};

		const TotalCapitalGains = Object.values(CapitalGains)?.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

		const DATA = {
			CapitalGains: CapitalGains,
			TotalCapitalGains: TotalCapitalGains
		};

		setValues({ ...cloneDeep(values), ...DATA });
		setFieldValue("IncomeDetails.OtherAssets", AnyOtherAssetsDetails);
		setFieldValue("LastSavedTimeStamp", new Date().getTime());
	};

	return (
		<>
			<section>
				<div className="section-title-wrap">
					<h5>Any Other Assets</h5>
					<PreventEditComponent>
						<Button variant="secondary" size="sm" onClick={() => setShowAddFormCanvas(() => true)}>
							<FASvgIcon icon={faPlus} />
							Add
						</Button>
					</PreventEditComponent>
				</div>

				<dl className="eds-list eds-dl eds-inline-between">
					{AnyOtherAssetsDetails.map((income, index) => {
						// if ImprovementExpenses is selected then,
						// subtract the ImprovementExpenses total from net total profit
						// ImprovementExpenses is a 2D array [["Tax","400"]]
						let sum = 0;
						if (income?.ImprovementExpenses?.length) {
							income?.ImprovementExpenses?.forEach((item) => {
								if (item.length && item[1]) {
									sum += parseInt(item[1]) || 0;
								}
							});
						}

						const profitOrLoss =
							Number(income.TotalSalePrice || 0) -
							Number(income.TotalPurchasePrice || 0) -
							Number(income.TransferExpense || 0) -
							sum;

						return (
							<div key={`any-asset-${index}`} className="eds-list--item">
								<dt>{income.Description}</dt>
								<dd>
									<span>
										<PriceDisplay price={profitOrLoss} />
									</span>
									<Button
										variant="link"
										onClick={() => {
											setShowAddFormCanvas(() => true);
											setActiveItem(() => income);
											setActiveItemIdx(() => String(index));
										}}
									>
										View
										<PreventEditComponent>
											<>/Edit</>
										</PreventEditComponent>
									</Button>
									<PreventEditComponent>
										<Button
											variant="transparent"
											className="icon-only"
											onClick={() => {
												setShowDeleteModal(() => income);
												setActiveItemIdx(() => String(index));
											}}
										>
											<FASvgIcon icon={faTrash} />
										</Button>
									</PreventEditComponent>
								</dd>
							</div>
						);
					})}
				</dl>
			</section>

			<OAAnyOtherAssetsDetailsForm
				showCanvas={showAddFormCanvas}
				handleCloseBtnPress={(assetData) => {
					if (assetData) {
						if (activeItem) {
							AnyOtherAssetsDetails.splice(Number(activeItemIdx), 1, assetData);
						} else {
							AnyOtherAssetsDetails.push(assetData);
						}
						handleTotalCalculation();
					}
					setShowAddFormCanvas(() => false);
					setActiveItem(() => null);
					setActiveItemIdx(() => "");
				}}
				formValues={activeItem}
			/>

			<ConfirmationModal
				showModal={!!showDeleteModal}
				iconColorClass="text-danger"
				title="Are you sure?"
				message={<p>You want to delete this</p>}
				onYesBtnClick={() => handleDeleteInterestItem()}
				onNoBtnClick={() => setShowDeleteModal(() => null)}
				icon={faTrashAlt}
			/>
		</>
	);
};

export default OAAnyOtherAssetsDetails;
